import { useCallback, useEffect, useState, useMemo, useRef } from "react";
import { Breadcrumb, message, Modal } from "antd";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors } from "@dnd-kit/core";
import {
	arrayMove,
	SortableContext,
	sortableKeyboardCoordinates,
	verticalListSortingStrategy
} from "@dnd-kit/sortable";
import { restrictToVerticalAxis } from "@dnd-kit/modifiers";
import { ApolloError } from "@apollo/client";

import GuideDetailsHeadCard from "components/guide-details-head-card/guide-details-head-card";
import GuideStep from "components/guide-step/guide-step";
import { useGetGuideQuery } from "graphql/queries/get-guide.generated";
import { useGetGuideCommentsQuery } from "graphql/queries/get-guide-comments.generated";
import { useUpdateGuideAndRelationshipsMutation } from "graphql/mutations/update-guide-and-relationships.generated";
import { useUpdateGuideByIdMutation } from "graphql/mutations/update-guide-by-id.generated";
import { useAddUserActivityMutation } from "graphql/mutations/add-user-activity.generated";

import { useLoggedUser } from "state/reactive-vars/logged-user-var";
import { GuideStatus, UserRoleValue, EventType, EntityType } from "constants/field-values";
import routeNames from "constants/route-names";
import { useUserActivityStore } from "state/zustand-stores/user-activity-store";

import { Guides } from "graphql/generated/graphql";
import GuideProcessing from "components/guide-processing/guide-processing";
import { useProcessingGuideSubscription } from "graphql/subscriptions/guide.generated";
import { useBrand } from "context/BrandContext";
import { RcFile } from "antd/es/upload";
import { sanitizeFileName, uploadToStorage, deleteFromStorage } from "utils/files-management";

export interface SelectedProduct {
	id: string;
	name: string;
	photo_link?: string | null;
	installed_products: Array<{
		id: string;
		property?: {
			id: string;
			name: string;
		} | null;
	}>;
}

const DeleteModal = ({ isDraft, isModalOpen, handleDelete, handleCancel }) => {
	const { brand } = useBrand();

	const title = isDraft ? "Are you sure you want to start over?" : "Delete Guide";
	const content = isDraft
		? "Leaving this screen to add a new video will permanently delete your current draft. Do you want to continue?"
		: "Are you sure you want to delete this Guide? This action cannot be undone.";

	return (
		<Modal
			rootClassName={`brand-${brand.toLowerCase()}`}
			className="guide-details-delete-guide-modal"
			title={title}
			open={isModalOpen}
			onOk={handleDelete}
			onCancel={handleCancel}
			footer={[
				<div className="highnote-button-white-wrapper">
					<button key="back" onClick={handleCancel}>
						Go Back
					</button>
				</div>,
				<button className="highnote-button" key="submit" onClick={handleDelete}>
					Continue
				</button>
			]}
			centered
		>
			<div>{content}</div>
		</Modal>
	);
};

const GuideDetails = ({ isUserEditing }: { isUserEditing?: boolean }) => {
	const navigate = useNavigate();
	const location = useLocation();
	const { pathname } = location;
	const { guideId } = useParams<{ guideId: string }>();
	const { loggedUserValue, isUserAnon } = useLoggedUser();

	const [guide, setGuide] = useState<any>(null);
	const [steps, setSteps] = useState<any>([]);
	const [isUploadingGuide, setIsUploadingGuide] = useState(false);
	const [isModalOpen, setIsModalOpen] = useState(false);
	const isDraft = guide?.status === GuideStatus.draft;
	const hasLoggedActivityRef = useRef(false);

	// Zustand store for tracking viewed guides across tabs
	const { hasViewedGuide, markGuideAsViewed } = useUserActivityStore();

	const canUserEdit = useMemo(() => {
		if (!guide) return false;
		return (
			loggedUserValue.role === UserRoleValue.admin_user ||
			guide.created_by_user_id === loggedUserValue.id ||
			(loggedUserValue.role === UserRoleValue.vendor_manager &&
				loggedUserValue.organization_id === guide.organization_id)
		);
	}, [guide, loggedUserValue]);

	useEffect(() => {
		if (guide && isUserEditing && !canUserEdit) {
			message.error("You do not have permission to edit this Guide.");
			navigate(pathname.replace(/\/edit$/, ""));
		}
	}, [guide, canUserEdit, navigate, pathname]);

	// guide details head card state
	const [title, setTitle] = useState<string>("");
	const [description, setDescription] = useState<string>("");
	const [selectedDevices, setSelectedDevices] = useState<SelectedProduct[]>([]);
	const [originalSteps, setOriginalSteps] = useState<any>([]);

	const navigateToDetails = () => {
		const newPath = pathname.replace(/\/edit$/, "");
		navigate(newPath);
	};

	const handleOnCompleteGetGuide = useCallback((data: any) => {
		console.log("useGetGuideQuery onCompleted data:", data);
		let editableSteps = data.guides_by_pk?.guide_steps ? [...data.guides_by_pk?.guide_steps] : [];

		const products: SelectedProduct[] = data.guides_by_pk?.product_guides.map(pg => pg?.product).filter(Boolean) || [];

		setGuide(data.guides_by_pk);
		setSteps(editableSteps);
		setOriginalSteps(editableSteps);
		setTitle(data.guides_by_pk?.title || "");
		setDescription(data.guides_by_pk?.description || "");
		setSelectedDevices(products);
	}, []);

	const handleOnErrorGetGuide = useCallback((error: ApolloError) => {
		console.error("Error fetching Guide:", error);
	}, []);

	const {
		data: guideData,
		loading,
		error,
		refetch
	} = useGetGuideQuery({
		variables: {
			id: guideId!,
			loggedUserId: loggedUserValue.id,
			isAnon: !loggedUserValue.id
		},
		skip: !guideId,
		onCompleted: handleOnCompleteGetGuide,
		onError: handleOnErrorGetGuide,
		fetchPolicy: "network-only"
	});

	// Fetch comments separately to avoid refetching guide and reloading ui
	const { data: commentsData } = useGetGuideCommentsQuery({
		variables: { guideId: guideId!, isAnon: false },
		skip: !guideId
	});

	const comments = commentsData?.guide_comments || [];

	const [updateGuideAndRelationships] = useUpdateGuideAndRelationshipsMutation({
		errorPolicy: "none",
		onCompleted: data => {
			console.log("updateGuideAndRelationships onCompleted data: ", data);
			message.success("Guide updated successfully!");
			refetch();

			if (guide?.handover_id) {
				navigate(routeNames.HANDOVER_DETAILS.replace(":handoverId", guide?.handover_id));
			} else {
				navigateToDetails();
			}
		},
		onError: err => {
			console.error("Error updating Guide:", err);
			message.error("Failed to update Guide. Please try again.");
		}
	});

	const [updateGuideByIdMutation, { loading: deleteLoading }] = useUpdateGuideByIdMutation({
		onCompleted: () => {
			message.success("Guide deleted successfully!");
			navigate(routeNames.GUIDES);
		},
		onError: err => {
			console.error("Error deleting Guide:", err);
			message.error("Failed to delete Guide. Please try again.");
		}
	});

	const [addUserActivityMutation] = useAddUserActivityMutation({
		onError: err => {
			console.error("Error logging user activity:", err);
		}
	});

	// Log user activity when guide is viewed (only on first view, not on refresh)
	useEffect(() => {
		if (guide && guideId && loggedUserValue.id && !hasLoggedActivityRef.current) {
			const alreadyViewed = hasViewedGuide(guideId, loggedUserValue.id);

			if (!alreadyViewed) {
				addUserActivityMutation({
					variables: {
						input: {
							user_id: loggedUserValue.id,
							entity: EntityType.GUIDES,
							entity_id: guideId,
							event_type: EventType.VIEW
						}
					}
				});
				markGuideAsViewed(guideId, loggedUserValue.id);
				hasLoggedActivityRef.current = true;
			}
		}
	}, [guide, guideId, loggedUserValue.id, hasViewedGuide, markGuideAsViewed, addUserActivityMutation]);

	const handleDeleteGuide = useCallback(async () => {
		try {
			await updateGuideByIdMutation({
				variables: { id: guideId, _set: { status: GuideStatus.deleted } }
			});

			if (guide.status === GuideStatus.draft) {
				navigate(routeNames.GUIDE_ADD);
			} else {
				navigate(routeNames.GUIDES);
			}
		} catch (error) {
			console.error("Error in handleDeleteGuide:", error);
		}
	}, [guideId, updateGuideByIdMutation]);

	// Only subscribe when guide exists and is in processing state
	const shouldSubscribe = guide?.status === GuideStatus.processing;

	useProcessingGuideSubscription({
		variables: { id: guideId! },
		skip: !guideId || !shouldSubscribe,
		onData: ({ data }) => {
			const subscriptionGuide = data.data?.guides_by_pk;
			if (subscriptionGuide?.status === GuideStatus.draft) {
				console.log("Highnote status changed to draft, refetching...");
				refetch();
			}
		}
	});

	// Helper function to upload step media files
	const uploadStepMediaFiles = useCallback(async (step: any) => {
		const uploadedMedia: { video_url?: string; image_url?: string } = {};

		// Upload video file if exists
		if (step.video_file) {
			try {
				const uniqueID = uuidv4();
				const folder = "training-guides-steps-videos/";
				const formattedFileName = sanitizeFileName(step.video_file.name);
				const fileName = `step-video-${Math.floor(Date.now() / 1000)}-${uniqueID}-${formattedFileName}`;

				const { publicUrl } = await uploadToStorage(step.video_file, () => {}, `${folder}/${fileName}`);
				uploadedMedia.video_url = publicUrl;
				console.log("Uploaded step video URL:", publicUrl);
			} catch (error) {
				console.error("Error uploading step video:", error);
				message.error(`Failed to upload video for step ${step.step_number}`);
				throw error;
			}
		}

		// Upload image file if exists
		if (step.image_file) {
			try {
				const uniqueID = uuidv4();
				const folder = "training-guides-steps-images/";
				const formattedFileName = sanitizeFileName(step.image_file.name);
				const fileName = `step-image-${Math.floor(Date.now() / 1000)}-${uniqueID}-${formattedFileName}`;

				const { publicUrl } = await uploadToStorage(step.image_file, () => {}, `${folder}/${fileName}`);
				uploadedMedia.image_url = publicUrl;
				console.log("Uploaded step image URL:", publicUrl);
			} catch (error) {
				console.error("Error uploading step image:", error);
				message.error(`Failed to upload image for step ${step.step_number}`);
				throw error;
			}
		}

		return uploadedMedia;
	}, []);

	// Helper function to delete media files from storage for deleted steps
	const deleteStepMediaFiles = useCallback(async (step: any) => {
		const deletionPromises: Promise<void>[] = [];

		// Delete video file if exists
		if (step.video_url) {
			deletionPromises.push(
				deleteFromStorage(step.video_url).catch(error => {
					console.error(`Failed to delete video for step ${step.step_number}:`, error);
					// Don't throw error to prevent blocking the save operation
				})
			);
		}

		// Delete image file if exists
		if (step.image_url) {
			deletionPromises.push(
				deleteFromStorage(step.image_url).catch(error => {
					console.error(`Failed to delete image for step ${step.step_number}:`, error);
					// Don't throw error to prevent blocking the save operation
				})
			);
		}

		// Wait for all deletions to complete (or fail)
		await Promise.all(deletionPromises);
	}, []);

	const handleSave = useCallback(async () => {
		if (!guideId) return;
		setIsUploadingGuide(true);

		try {
			// Upload media files for steps that have them and delete old media if being replaced
			const stepsWithUploadedMedia = await Promise.all(
				steps.map(async step => {
					// Find the original step to check for old media that needs deletion
					const originalStep = originalSteps.find(orig => orig.id === step.id);

					const uploadedMedia = await uploadStepMediaFiles(step);

					// If we uploaded new media and there was old media, delete the old files
					// or if the media was removed, delete the file
					if (originalStep) {
						if (
							(uploadedMedia.video_url && originalStep.video_url) ||
							(!uploadedMedia.video_url && originalStep.video_url)
						) {
							deleteFromStorage(originalStep.video_url).catch(error => {
								console.error(`Failed to delete old video for step ${step.step_number}:`, error);
							});
						}
						if (
							(uploadedMedia.image_url && originalStep.image_url) ||
							(!uploadedMedia.image_url && originalStep.image_url)
						) {
							deleteFromStorage(originalStep.image_url).catch(error => {
								console.error(`Failed to delete old image for step ${step.step_number}:`, error);
							});
						}
					}

					return {
						...step,
						...uploadedMedia
					};
				})
			);

			// Prepare steps for insertion/update
			const cleanedSteps = stepsWithUploadedMedia.map((step, index) => {
				const { __typename, id, video_file, image_file, ...stepData } = step;
				return {
					...stepData,
					guide_id: guideId,
					step_number: index + 1,
					...(typeof step.id === "string" && { id: step.id })
				};
			});

			// Find steps to delete (steps that were in original but not in current)
			const deleteStepIds = originalSteps
				.filter(orig => !steps.some(s => s.id === orig.id))
				.map(s => s.id)
				.filter((id): id is string => typeof id === "string");

			// Get the full step objects for deletion to access their media URLs
			const stepsToDelete = originalSteps.filter(orig => !steps.some(s => s.id === orig.id));

			// Delete media files for steps that will be deleted
			await Promise.all(
				stepsToDelete.map(async step => {
					await deleteStepMediaFiles(step);
				})
			);

			// Find new product guides to insert
			const currentProductIds = guide?.product_guides?.map(pg => pg.product?.id).filter(Boolean) || [];

			// Get IDs of selected products
			const selectedProductIds = selectedDevices.map(device => device.id);

			// Find new product guides to insert
			const newProductGuides = selectedProductIds
				.filter(deviceId => !currentProductIds.includes(deviceId))
				.map(deviceId => ({ guide_id: guideId, product_id: deviceId }));

			// Find product guides to delete
			const deleteProductGuideIds =
				guide?.product_guides
					?.filter(pg => !selectedProductIds.includes(pg.product?.id))
					.map(pg => pg.id)
					.filter(Boolean) || [];

			const variables = {
				id: guideId,
				_set: {
					title,
					description,
					status: GuideStatus.published, // Set status to 'published' on save
					updated_at: new Date().toISOString()
				},
				steps: cleanedSteps,
				deleteStepIds: deleteStepIds,
				productGuides: newProductGuides,
				deleteProductGuideIds: deleteProductGuideIds
			};

			await updateGuideAndRelationships({ variables });
		} catch (error) {
			console.error("Error in saveGuideChanges:", error);
			throw error; // Re-throw to allow GuideDetailsHeadCard to handle error
		} finally {
			setIsUploadingGuide(false);
		}
	}, [
		guideId,
		updateGuideAndRelationships,
		steps,
		originalSteps,
		title,
		description,
		selectedDevices,
		guide?.product_guides,
		uploadStepMediaFiles,
		deleteStepMediaFiles,
		guide?.handover_id
	]);

	const sensors = useSensors(
		useSensor(PointerSensor),
		useSensor(KeyboardSensor, {
			coordinateGetter: sortableKeyboardCoordinates
		})
	);

	const updateStepTitle = useCallback((stepId: number | string, newTitle: string) => {
		setSteps(prevSteps => prevSteps.map(step => (step.id === stepId ? { ...step, title: newTitle } : step)));
	}, []);

	const updateStepDescription = useCallback((stepId: number | string, newDescription: string) => {
		setSteps(prevSteps => prevSteps.map(step => (step.id === stepId ? { ...step, body: newDescription } : step)));
	}, []);

	const updateStepMedia = useCallback(
		(stepId: number | string, newMedia: { image_file?: RcFile; video_file?: RcFile }, removeMedia?: boolean) => {
			setSteps(prevSteps =>
				prevSteps.map(step =>
					step.id === stepId
						? {
								...step,
								...newMedia,
								...(removeMedia ? { image_url: null, video_url: null } : {})
							}
						: step
				)
			);
		},
		[]
	);

	const addStepAtIndex = useCallback((index: number) => {
		const newStep: any = {
			title: "Add a title for this step...",
			body: "<ul><li>Add a description or instructions for this step...</li></ul>",
			id: Date.now(), // Unique ID for the new step, won't be saved to db
			step_number: index + 1,
			image_url: null,
			video_url: null,
			start_time: null,
			end_time: null
		};

		setSteps(prevSteps => {
			const updatedSteps = [...prevSteps];
			updatedSteps.splice(index, 0, newStep);
			// Update step numbers for all subsequent steps
			return updatedSteps.map((step, idx) => ({
				...step,
				step_number: idx + 1
			}));
		});
	}, []);

	const deleteStepAtIndex = useCallback(
		(index: number) => {
			if (steps.length <= 1) {
				message.warning("You must have at least one step in your Guide.");
				return;
			}

			const stepToDelete = steps[index];
			const updatedSteps = [...steps];
			updatedSteps.splice(index, 1);

			// Update step numbers for remaining steps
			const reNumberedSteps = updatedSteps.map((step, idx) => ({
				...step,
				step_number: idx + 1
			}));

			setSteps(reNumberedSteps);

			// If deleting an existing step (has string ID), keep track for deletion in mutation
			if (typeof stepToDelete.id === "string") {
				// The deletion will be handled by the save function by comparing originalSteps vs current steps
				// No need to modify originalSteps here since we want to track what was originally there
			}
		},
		[steps]
	);

	const handleDragEnd = useCallback(event => {
		const { active, over } = event;

		if (active.id !== over.id) {
			setSteps(prevSteps => {
				const oldIndex = prevSteps.findIndex(item => item.id === active.id);
				const newIndex = prevSteps.findIndex(item => item.id === over.id);

				const reorderedSteps = arrayMove(prevSteps, oldIndex, newIndex);

				// Update step numbers after reordering
				return reorderedSteps.map((step: any, idx) => ({
					...step,
					step_number: idx + 1
				}));
			});
		}
	}, []);

	if (loading) {
		return (
			<div className="products-details-screen-container">
				<div>Loading Guide...</div>
			</div>
		);
	}

	if (error || !guide) {
		return (
			<div className="products-details-screen-container">
				<div>Guide not found</div>
			</div>
		);
	}

	if (guide.status === GuideStatus.processing) {
		return <GuideProcessing />;
	}

	return (
		<div className="products-details-screen-container">
			<Breadcrumb
				className="products-breadcrumbs"
				items={[
					{
						title: "Guides",
						onClick: () => navigate("/guides")
					},
					{
						title: guide.title,
						onClick: isUserEditing ? navigateToDetails : undefined
					},
					...(isUserEditing ? [{ title: "Edit" }] : [])
				]}
			/>
			<div className="guide-details-container" style={{ paddingBottom: isDraft && isUserEditing ? "104px" : "28px" }}>
				<GuideDetailsHeadCard
					isUserEditing={isUserEditing && canUserEdit}
					guide={guide}
					comments={comments}
					onSave={handleSave}
					onDelete={handleDeleteGuide}
					updateLoading={isUploadingGuide}
					deleteLoading={deleteLoading}
					stateManagement={{
						title,
						setTitle,
						description,
						setDescription,
						selectedDevices,
						setSelectedDevices
					}}
				/>

				{/* Add step button at the beginning (only show if editing) */}
				{isUserEditing && canUserEdit && (
					<div className="guide-details-add-step-container">
						<button onClick={() => addStepAtIndex(0)} className="guide-details-add-step-button">
							<img src={`${process.env.PUBLIC_URL}/assets/images/icons/plus-purple.svg`} alt="Plus Icon" />
							Step
						</button>
					</div>
				)}

				{/* Conditional rendering based on edit mode */}
				{isUserEditing && canUserEdit ? (
					<DndContext
						sensors={sensors}
						collisionDetection={closestCenter}
						onDragEnd={handleDragEnd}
						modifiers={[restrictToVerticalAxis]}
					>
						<SortableContext items={steps} strategy={verticalListSortingStrategy}>
							{steps.map((step, index) => (
								<div className="guide-details-step-edit" key={`step-${step.id}`}>
									<GuideStep
										step={step}
										guide={guideData?.guides_by_pk as Guides}
										stepNumber={index + 1}
										isUserEditing
										onDelete={() => deleteStepAtIndex(index)}
										onTitleChange={newTitle => updateStepTitle(step.id, newTitle)}
										onDescriptionChange={newDescription => updateStepDescription(step.id, newDescription)}
										onMediaChange={(newMedia, removeMedia) => updateStepMedia(step.id, newMedia, removeMedia)}
									/>
									<div className="guide-details-add-step-container">
										<button onClick={() => addStepAtIndex(index + 1)} className="guide-details-add-step-button">
											<img src={`${process.env.PUBLIC_URL}/assets/images/icons/plus-purple.svg`} alt="Plus Icon" />
											Step
										</button>
									</div>
								</div>
							))}
						</SortableContext>
					</DndContext>
				) : (
					/* View mode - simple rendering without drag and drop */
					steps.map(step => (
						<GuideStep
							key={step.id}
							guide={guideData?.guides_by_pk as Guides}
							comments={comments}
							stepNumber={step.step_number}
							step={step}
						/>
					))
				)}
			</div>

			{isUserEditing && canUserEdit && isDraft && (
				<div className="guide-details-draft-toolbar">
					<span>Publish your Guide or start over with a new one.</span>
					<div className="guide-details-draft-toolbar-buttons">
						<div className="highnote-button-white-wrapper">
							<button className="guide-details-start-over-button" onClick={() => setIsModalOpen(true)}>
								Start Over
							</button>
						</div>
						<button className="highnote-button" onClick={handleSave}>
							Publish Guide
						</button>
					</div>
				</div>
			)}

			<DeleteModal
				isDraft={isDraft}
				isModalOpen={isModalOpen}
				handleDelete={handleDeleteGuide}
				handleCancel={() => setIsModalOpen(false)}
			/>
		</div>
	);
};

export default GuideDetails;
